import { formatMoney, mapSelfServiceTypes, moneyToCents } from '../../../utils';
import {
  SERVICE_TYPE_TO_LOCATION_KEY,
  SELF_SERVICE_MODES,
  VACUUM_INTERVALS,
} from './bulkPricing.constants';

const MONTH_NAMES = [
  'Sausis',
  'Vasaris',
  'Kovas',
  'Balandis',
  'Gegužė',
  'Birželis',
  'Liepa',
  'Rugpjūtis',
  'Rugsėjis',
  'Spalis',
  'Lapkritis',
  'Gruodis',
];

export const getLocationServiceBlock = (location, serviceType) => {
  const key = SERVICE_TYPE_TO_LOCATION_KEY[serviceType];
  return key ? location?.[key] : null;
};

const parseNumber = value => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatEurFromCents = cents => {
  if (cents === null || cents === undefined || cents === '') {
    return '-';
  }
  return formatMoney(Number(cents));
};

const formatEurFromEuros = euros => {
  if (euros === null || euros === undefined || euros === '') {
    return '-';
  }
  const value = Number(euros);
  if (Number.isNaN(value)) {
    return '-';
  }
  return `${value.toLocaleString('lt-LT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} eur`;
};

const formatSeconds = value => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return `${value}s`;
};

const formatMinutes = value => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return `${value} min`;
};

const formatMonth = month => {
  if (!month) {
    return '-';
  }
  return MONTH_NAMES[month - 1] || String(month);
};

export const getChangedFieldKeys = touched =>
  Object.keys(touched).filter(key => touched[key]);

export const hasAnyChanges = touched => getChangedFieldKeys(touched).length > 0;

export const isVacuumBulkPricingComplete = formValues =>
  VACUUM_INTERVALS.every(interval => {
    const value = formValues[`${interval.id}Price`];
    return value !== '' && value !== null && value !== undefined;
  });

const getSelfServiceOption = (serviceBlock, optionType) =>
  serviceBlock?.options?.find(option => option.type === optionType);

const readCurrentSelfService = (serviceBlock, mode) => {
  const option = getSelfServiceOption(serviceBlock, mode.optionType);
  return {
    priceCents: option?.price ?? null,
    duration: option?.time ?? null,
  };
};

const readCurrentVacuumInterval = (serviceBlock, optionType) => {
  const option = serviceBlock?.options?.find(o => o.type === optionType);
  return {
    priceCents: option?.price ?? null,
  };
};

const readCurrentWindowLiquid = (serviceBlock, optionType) => {
  const option = serviceBlock?.options?.find(o => o.type === optionType);
  return {
    priceCents: option?.price ?? null,
    month: option?.month ?? null,
  };
};

const readCurrentPriceOnly = serviceBlock => ({
  priceCents: serviceBlock?.options?.[0]?.price ?? null,
});

const readCurrentMatOrTyre = serviceBlock => ({
  priceCents: serviceBlock?.options?.[0]?.price ?? null,
  durationSeconds: serviceBlock?.time ?? null,
});

export const buildBulkUpdatePayload = ({
  serviceType,
  locationIds,
  formValues,
  touched,
}) => {
  if (!locationIds?.length || !SERVICE_TYPE_TO_LOCATION_KEY[serviceType]) {
    return null;
  }

  const data = {};

  switch (serviceType) {
    case 21: {
      const options = [];
      SELF_SERVICE_MODES.forEach(mode => {
        const priceTouched = touched[`${mode.id}Price`];
        const durationTouched = touched[`${mode.id}Duration`];
        if (!priceTouched && !durationTouched) {
          return;
        }
        const option = { type: mode.optionType };
        if (priceTouched) {
          option.price = moneyToCents(formValues[`${mode.id}Price`]);
        }
        if (durationTouched) {
          option.time = parseNumber(formValues[`${mode.id}Duration`]) ?? 0;
        }
        options.push(option);
      });
      if (options.length) {
        data.options = options;
      }
      break;
    }
    case 24: {
      const anyTouched = VACUUM_INTERVALS.some(
        interval => touched[`${interval.id}Price`]
      );
      if (anyTouched) {
        data.options = VACUUM_INTERVALS.map(interval => ({
          type: interval.optionType,
          price: moneyToCents(formValues[`${interval.id}Price`]),
        }));
      }
      break;
    }
    case 26: {
      const options = [];
      if (touched.summerPrice || touched.summerMonth) {
        const entry = { type: 601 };
        if (touched.summerPrice) {
          entry.price = moneyToCents(formValues.summerPrice);
        }
        if (touched.summerMonth) {
          entry.month = parseNumber(formValues.summerMonth) ?? 1;
        }
        options.push(entry);
      }
      if (touched.winterPrice || touched.winterMonth) {
        const entry = { type: 602 };
        if (touched.winterPrice) {
          entry.price = moneyToCents(formValues.winterPrice);
        }
        if (touched.winterMonth) {
          entry.month = parseNumber(formValues.winterMonth) ?? 1;
        }
        options.push(entry);
      }
      if (options.length) {
        data.options = options;
      }
      break;
    }
    case 25: {
      if (touched.price) {
        data.options = [
          {
            type: 501,
            price: moneyToCents(formValues.price),
          },
        ];
      }
      break;
    }
    case 27: {
      const options = [];
      if (touched.price) {
        options.push({
          type: 401,
          price: moneyToCents(formValues.price),
        });
      }
      if (touched.duration) {
        data.time = (parseNumber(formValues.duration) ?? 0) * 60;
      }
      if (options.length) {
        data.options = options;
      }
      break;
    }
    case 29: {
      const options = [];
      if (touched.price) {
        options.push({
          type: 701,
          price: moneyToCents(formValues.price),
        });
      }
      if (touched.duration) {
        data.time = parseNumber(formValues.duration) ?? 0;
      }
      if (options.length) {
        data.options = options;
      }
      break;
    }
    default:
      return null;
  }

  const hasUpdates =
    (data.options && data.options.length > 0) || data.time !== undefined;

  if (!hasUpdates) {
    return null;
  }

  return {
    service_type: serviceType,
    location_ids: locationIds,
    data,
  };
};

const groupLocationsByOldValues = (entries, getOldValues) => {
  const groups = new Map();

  entries.forEach(entry => {
    const oldValues = getOldValues(entry);
    const signature = JSON.stringify(oldValues);
    if (!groups.has(signature)) {
      groups.set(signature, { oldValues, locations: [] });
    }
    groups.get(signature).locations.push(entry);
  });

  return Array.from(groups.values());
};

const buildSelfServicePreview = ({ locations, formValues, touched }) => {
  const rows = [];

  SELF_SERVICE_MODES.forEach(mode => {
    const priceTouched = touched[`${mode.id}Price`];
    const durationTouched = touched[`${mode.id}Duration`];
    if (!priceTouched && !durationTouched) {
      return;
    }

    const entries = locations
      .map(location => {
        const serviceBlock = getLocationServiceBlock(location, 21);
        if (!serviceBlock) {
          return null;
        }
        const current = readCurrentSelfService(serviceBlock, mode);
        return {
          location,
          current,
        };
      })
      .filter(Boolean);

    if (!entries.length) {
      return;
    }

    const newPriceCents = priceTouched
      ? Math.round((parseNumber(formValues[`${mode.id}Price`]) ?? 0) * 100)
      : null;
    const newDuration = durationTouched
      ? parseNumber(formValues[`${mode.id}Duration`])
      : null;

    const priceVaries =
      priceTouched && new Set(entries.map(e => e.current.priceCents)).size > 1;
    const durationVaries =
      durationTouched && new Set(entries.map(e => e.current.duration)).size > 1;

    const groups = groupLocationsByOldValues(entries, e => ({
      priceCents: e.current.priceCents,
      duration: e.current.duration,
    }));

    groups.forEach(group => {
      rows.push({
        serviceLabel: mapSelfServiceTypes(mode.optionType),
        locations: group.locations.map(e => e.location),
        oldPrice: priceTouched
          ? formatEurFromCents(group.oldValues.priceCents)
          : '-',
        newPrice: priceTouched ? formatEurFromCents(newPriceCents) : '-',
        oldDuration: durationTouched
          ? formatSeconds(group.oldValues.duration)
          : '-',
        newDuration: durationTouched ? formatSeconds(newDuration) : '-',
        priceVaries,
        durationVaries,
      });
    });
  });

  return rows;
};

const buildVacuumPreview = ({ locations, formValues, touched }) => {
  const rows = [];
  const anyTouched = VACUUM_INTERVALS.some(
    interval => touched[`${interval.id}Price`]
  );
  if (!anyTouched) {
    return rows;
  }

  VACUUM_INTERVALS.forEach(interval => {
    const entries = locations
      .map(location => {
        const serviceBlock = getLocationServiceBlock(location, 24);
        if (!serviceBlock) {
          return null;
        }
        return {
          location,
          current: readCurrentVacuumInterval(serviceBlock, interval.optionType),
        };
      })
      .filter(Boolean);

    if (!entries.length) {
      return;
    }

    const newPriceCents = Math.round(
      (parseNumber(formValues[`${interval.id}Price`]) ?? 0) * 100
    );
    const priceVaries =
      new Set(entries.map(e => e.current.priceCents)).size > 1;

    const groups = groupLocationsByOldValues(entries, e => ({
      priceCents: e.current.priceCents,
    }));

    groups.forEach(group => {
      rows.push({
        serviceLabel: interval.label,
        locations: group.locations.map(e => e.location),
        oldPrice: formatEurFromCents(group.oldValues.priceCents),
        newPrice: formatEurFromCents(newPriceCents),
        oldDuration: '-',
        newDuration: '-',
        priceVaries,
        durationVaries: false,
      });
    });
  });

  return rows;
};

const buildSimplePriceDurationPreview = ({
  locations,
  serviceType,
  formValues,
  touched,
  serviceLabel,
  readCurrent,
  durationUnit,
}) => {
  const rows = [];
  const entries = locations
    .map(location => {
      const serviceBlock = getLocationServiceBlock(location, serviceType);
      if (!serviceBlock) {
        return null;
      }
      return { location, current: readCurrent(serviceBlock) };
    })
    .filter(Boolean);

  if (!entries.length) {
    return rows;
  }

  const newPriceCents = touched.price
    ? Math.round((parseNumber(formValues.price) ?? 0) * 100)
    : null;
  const newDuration = touched.duration
    ? parseNumber(formValues.duration)
    : null;
  const priceVaries =
    touched.price && new Set(entries.map(e => e.current.priceCents)).size > 1;
  const durationVaries =
    touched.duration &&
    new Set(entries.map(e => e.current.durationSeconds)).size > 1;

  const formatDuration =
    durationUnit === 'minutes' ? formatMinutes : formatSeconds;

  const groups = groupLocationsByOldValues(entries, e => ({
    priceCents: e.current.priceCents,
    durationSeconds: e.current.durationSeconds,
  }));

  groups.forEach(group => {
    rows.push({
      serviceLabel,
      locations: group.locations.map(e => e.location),
      oldPrice: touched.price
        ? formatEurFromCents(group.oldValues.priceCents)
        : '-',
      newPrice: touched.price ? formatEurFromCents(newPriceCents) : '-',
      oldDuration: touched.duration
        ? formatDuration(
            durationUnit === 'minutes'
              ? group.oldValues.durationSeconds / 60
              : group.oldValues.durationSeconds
          )
        : '-',
      newDuration: touched.duration ? formatDuration(newDuration) : '-',
      priceVaries,
      durationVaries,
    });
  });

  return rows;
};

export const buildPreviewRows = ({
  locations,
  serviceType,
  formValues,
  touched,
}) => {
  switch (serviceType) {
    case 21:
      return buildSelfServicePreview({ locations, formValues, touched });
    case 24:
      return buildVacuumPreview({ locations, formValues, touched });
    case 26: {
      const rows = [];
      const entries = locations
        .map(location => {
          const serviceBlock = getLocationServiceBlock(location, 26);
          if (!serviceBlock) {
            return null;
          }
          return { location, serviceBlock };
        })
        .filter(Boolean);

      const addSeasonRow = ({
        label,
        optionType,
        priceKey,
        monthKey,
        priceTouched,
        monthTouched,
      }) => {
        if (!priceTouched && !monthTouched) {
          return;
        }

        const seasonEntries = entries.map(({ location, serviceBlock }) => ({
          location,
          current: readCurrentWindowLiquid(serviceBlock, optionType),
        }));

        if (priceTouched) {
          const newPriceCents = Math.round(
            (parseNumber(formValues[priceKey]) ?? 0) * 100
          );
          const priceVaries =
            new Set(seasonEntries.map(e => e.current.priceCents)).size > 1;
          const groups = groupLocationsByOldValues(
            seasonEntries,
            e => e.current.priceCents
          );

          groups.forEach(group => {
            rows.push({
              serviceLabel: label,
              locations: group.locations.map(e => e.location),
              oldPrice: formatEurFromCents(group.oldValues),
              newPrice: formatEurFromCents(newPriceCents),
              oldDuration: '-',
              newDuration: '-',
              priceVaries,
              durationVaries: false,
            });
          });
        }

        if (monthTouched) {
          const newMonth = parseNumber(formValues[monthKey]);
          const durationVaries =
            new Set(seasonEntries.map(e => e.current.month)).size > 1;
          const groups = groupLocationsByOldValues(
            seasonEntries,
            e => e.current.month
          );

          groups.forEach(group => {
            rows.push({
              serviceLabel: label,
              locations: group.locations.map(e => e.location),
              oldPrice: '-',
              newPrice: '-',
              oldDuration: formatMonth(group.oldValues),
              newDuration: formatMonth(newMonth),
              priceVaries: false,
              durationVaries,
            });
          });
        }
      };

      addSeasonRow({
        label: 'Vasarinis langų skystis',
        optionType: 601,
        priceKey: 'summerPrice',
        monthKey: 'summerMonth',
        priceTouched: touched.summerPrice,
        monthTouched: touched.summerMonth,
      });
      addSeasonRow({
        label: 'Žieminis langų skystis',
        optionType: 602,
        priceKey: 'winterPrice',
        monthKey: 'winterMonth',
        priceTouched: touched.winterPrice,
        monthTouched: touched.winterMonth,
      });

      return rows;
    }
    case 25: {
      if (!touched.price) {
        return [];
      }
      const entries = locations
        .map(location => {
          const serviceBlock = getLocationServiceBlock(location, 25);
          if (!serviceBlock) {
            return null;
          }
          return {
            location,
            current: readCurrentPriceOnly(serviceBlock),
          };
        })
        .filter(Boolean);
      const newPriceCents = Math.round(
        (parseNumber(formValues.price) ?? 0) * 100
      );
      const priceVaries =
        new Set(entries.map(e => e.current.priceCents)).size > 1;
      const groups = groupLocationsByOldValues(
        entries,
        e => e.current.priceCents
      );

      return groups.map(group => ({
        serviceLabel: 'Šluostės',
        locations: group.locations.map(e => e.location),
        oldPrice: formatEurFromCents(group.oldValues),
        newPrice: formatEurFromCents(newPriceCents),
        oldDuration: '-',
        newDuration: '-',
        priceVaries,
        durationVaries: false,
      }));
    }
    case 27:
      return buildSimplePriceDurationPreview({
        locations,
        serviceType: 27,
        formValues,
        touched,
        serviceLabel: 'Kilimėlių plovykla',
        readCurrent: readCurrentMatOrTyre,
        durationUnit: 'minutes',
      });
    case 29:
      return buildSimplePriceDurationPreview({
        locations,
        serviceType: 29,
        formValues,
        touched,
        serviceLabel: 'Padangų juodinimas',
        readCurrent: readCurrentMatOrTyre,
        durationUnit: 'seconds',
      });
    default:
      return [];
  }
};

export const formatLocationAddressList = locations => {
  if (!locations?.length) {
    return '';
  }
  if (locations.length === 1) {
    return locations[0].full_address || locations[0].address || '-';
  }
  return locations
    .map(loc => loc.full_address || loc.address)
    .filter(Boolean)
    .join(', ');
};

export const formatTablePrice = value => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'number' && value > 100) {
    return formatMoney(value);
  }
  return formatEurFromEuros(value);
};

export const formatTableDuration = value => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
};
